import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Loader2, ShieldCheck, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { createStaff, listStaff } from "@/lib/api";

const StaffBadge = ({ activated, status }) => {
  if (status && status !== "active") {
    return <span className="shrink-0 rounded-full bg-[#fbe9e7] px-2.5 py-1 text-[11px] font-semibold text-brand-danger">{status}</span>;
  }
  return activated ? (
    <span className="shrink-0 rounded-full bg-brand-teal-soft px-2.5 py-1 text-[11px] font-semibold text-brand">Active</span>
  ) : (
    <span className="shrink-0 rounded-full bg-[#fbf1dd] px-2.5 py-1 text-[11px] font-semibold text-brand-gold">Invite pending</span>
  );
};

export const AdminStaff = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);

  const query = useQuery({ queryKey: ["staff"], queryFn: listStaff });

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      const result = await createStaff({ name: form.name.trim(), email: form.email.trim() });
      setInvite({ email: result.email, link: result.activation_link, emailed: result.emailed, promoted: result.status === "promoted" });
      toast.success(result.status === "promoted"
        ? `${result.email} promoted to admin`
        : `Admin invite created for ${result.email}`);
      setForm({ name: "", email: "" });
      query.refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create admin");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(invite.link);
      toast.success("Activation link copied");
    } catch {
      toast.error("Could not copy — select and copy the link manually");
    }
  };

  const items = query.data || [];

  return (
    <div className="space-y-6" data-testid="admin-staff">
      <section className="premium-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">ACCESS CONTROL</p>
            <h2>Add an admin</h2>
            <p>Add a new staff login or promote an existing account by email. Promotion replaces the account's current roles with admin access and signs it out on all devices.</p>
          </div>
        </div>

        <form onSubmit={submit} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
          <Input
            value={form.name}
            onChange={update("name")}
            placeholder="Full name"
            className="h-10 w-full min-w-0 rounded-[10px] border-black/10 bg-white"
            data-testid="staff-name"
          />
          <Input
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="Email address"
            className="h-10 w-full min-w-0 rounded-[10px] border-black/10 bg-white"
            data-testid="staff-email"
          />
          <Button
            type="submit"
            disabled={busy}
            className="h-10 rounded-[10px] bg-brand-forest text-white"
            data-testid="staff-create"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : <><UserPlus size={16} className="mr-1.5" /> Add admin</>}
          </Button>
        </form>

        {invite && (
          <div className="mt-4 min-w-0 rounded-[12px] border border-[#237a6e]/20 bg-brand-teal-soft p-4" data-testid="staff-invite">
            <p className="min-w-0 break-all text-[13px] font-semibold text-[#0f3d37]">
              {invite.promoted ? `${invite.email} promoted to admin.` : `Invite created for ${invite.email}.`}
              {invite.link && (invite.emailed ? " Activation email sent." : " Email not sent; share this link:")}
            </p>
            {invite.link ? <>
              <div className="mt-2 flex min-w-0 items-center gap-2">
              <Input readOnly value={invite.link} className="h-9 w-full min-w-0 rounded-[10px] border-black/10 bg-white text-[12px]" />
              <Button size="sm" variant="outline" onClick={copyLink} className="h-9 shrink-0 rounded-[10px]">
                <Copy size={14} className="mr-1" /> Copy
              </Button>
              </div>
              <p className="mt-2 text-[11px] text-brand-muted">This link lets the invitee set their password. It expires in 7 days.</p>
            </> : (
              <p className="mt-2 text-[13px] text-brand-muted">Their previous sessions have ended. They can sign in again with their existing login details.</p>
            )}
          </div>
        )}
      </section>

      <section className="premium-card table-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">DIRECTORY</p>
            <h2>Admins</h2>
            <p>Everyone with Greentek Staff access.</p>
          </div>
          {!query.isLoading && (
            <span className="rounded-full bg-brand-gold-soft px-3 py-1 text-[13px] font-bold text-brand-gold-deep">
              {items.length} {items.length === 1 ? "admin" : "admins"}
            </span>
          )}
        </div>

        {query.isLoading ? (
          <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-brand-muted" size={22} /></div>
        ) : items.length ? (
          <div className="space-y-2">
            {items.map((s) => (
              <div key={s.user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-black/5 bg-brand-surface p-3.5">
                <div className="flex min-w-0 flex-1 items-center gap-2.5">
                  <ShieldCheck size={18} className="shrink-0 text-brand" />
                  <div className="min-w-0">
                    <b className="block break-all text-[14px]">{s.email}</b>
                    <div className="mt-0.5 text-[12px] text-brand-muted">
                      Added {new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                    <div data-testid="staff-phone" className="text-[12px] text-brand-muted">
                      {s.phone ? <>{s.phone} <span className={`text-[11px] font-medium ${s.phone_verified ? "text-brand" : "text-amber-700"}`}>{s.phone_verified ? "verified" : "not verified"}</span></> : "No phone on record"}
                    </div>
                  </div>
                </div>
                <StaffBadge activated={s.activated} status={s.status} />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-inline"><span>No admins yet.</span></div>
        )}
      </section>
    </div>
  );
};
